/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo } from 'react';
import type { InvalidateQueryFilters, Query } from '@tanstack/react-query';
import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type {
    CreateQueryImperativeHelpers,
    CreateQueryImperativeHelpersWithCacheKey,
    CreateQueryImperativeHelpersWithCacheKeyAndCondition,
    CreateQueryImperativeHelpersWithCondition,
    CreateQueryOptions,
    CreateQueryOptionsWithCacheKey,
    CreateQueryOptionsWithCacheKeyAndCondition,
    CreateQueryOptionsWithCondition,
    QueryState,
    UsePrefetchQuery,
    UseQuery,
    UseQueryWithCacheKey,
    UseSuspenseQuery,
} from './createQueryTypes';
import type { GetPayloadArg, GetReturned, PayloadCreatorBaseType } from '../utils/internalTypings';
import { getQueryClient } from '../queryClientSingleton';
import { withSamePromiseWhilePending } from '../paginationEntity/createPaginationEntityFetchHelpers';
import { fallbackToNull } from '../utils/fallbackToNull';
import { useDynamicEnabled } from '../utils/useDynamicEnabled';
import type { GetSliceId } from '../utils/sliceSelectorsTypes';
import { createRetryHandler, getRetryOnMountInCaseOfError } from '../utils/retryConfigs';
import { createPlaceholderDataHandler } from '../utils/createPlaceholderDataHandler';
import { getCreateQueryDefaultOptions } from './initQuery';
import { getQueryKey } from './utils';

export function createQuery<PayloadCreator extends PayloadCreatorBaseType>(
    options: CreateQueryOptionsWithCacheKeyAndCondition<PayloadCreator>,
): [
    UseQueryWithCacheKey<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    CreateQueryImperativeHelpersWithCacheKeyAndCondition<
        GetPayloadArg<PayloadCreator>,
        GetReturned<PayloadCreator>
    >,
    UseSuspenseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator> | undefined>,
    UsePrefetchQuery<GetPayloadArg<PayloadCreator>>,
];
export function createQuery<PayloadCreator extends PayloadCreatorBaseType>(
    options: CreateQueryOptionsWithCacheKey<PayloadCreator>,
): [
    UseQueryWithCacheKey<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    CreateQueryImperativeHelpersWithCacheKey<
        GetPayloadArg<PayloadCreator>,
        GetReturned<PayloadCreator>
    >,
    UseSuspenseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    UsePrefetchQuery<GetPayloadArg<PayloadCreator>>,
];
export function createQuery<PayloadCreator extends PayloadCreatorBaseType>(
    options: CreateQueryOptionsWithCondition<PayloadCreator>,
): [
    UseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    CreateQueryImperativeHelpersWithCondition<
        GetPayloadArg<PayloadCreator>,
        GetReturned<PayloadCreator>
    >,
    UseSuspenseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator> | undefined>,
    UsePrefetchQuery<GetPayloadArg<PayloadCreator>>,
];
export function createQuery<PayloadCreator extends PayloadCreatorBaseType>(
    options: CreateQueryOptions<PayloadCreator>,
): [
    UseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    CreateQueryImperativeHelpers<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    UseSuspenseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>,
    UsePrefetchQuery<GetPayloadArg<PayloadCreator>>,
];
/**
 * Creates a custom query hook and helper functions for data fetching and state management.
 *
 * @template PayloadCreator - A function type that defines the asynchronous data fetching operation.
 *
 * @param {Object} options - Configuration options for creating the query.
 * @param {PayloadCreator} options.payloadCreator - A function that returns a promise with the query result.
 * @param {string} options.name - A unique name for the query, used for caching and action types.
 * @param {number} [options.cacheDuration] - Duration in milliseconds after which the data is considered stale.
 * @param {function} [options.condition] - A function that determines if the query should execute.
 * @param {function} [options.cacheKey] - A function that generates a unique cache key for the query.
 *
 * @returns {[UseQuery, QueryHelpers]} A tuple containing:
 *   1. A custom hook for using the query in components.
 *   2. An object with imperative helper functions for managing the query outside of React components.
 *
 * @example
 * // Basic usage
 * const [useUserQuery, userQueryHelpers] = createQuery({
 *   name: 'user',
 *   payloadCreator: (userId: string) => fetchUser(userId),
 * });
 *
 * // Usage with cache key
 * const [usePostQuery, postQueryHelpers] = createQuery({
 *   name: 'post',
 *   payloadCreator: (postId: string) => fetchPost(postId),
 *   cacheKey: (postId) => postId,
 * });
 *
 * // Usage with condition
 * const [useConditionalQuery, conditionalQueryHelpers] = createQuery({
 *   name: 'conditional',
 *   payloadCreator: (data: SomeType) => fetchConditional(data),
 *   condition: (data, state) => state.someFlag === true,
 * });
 *
 * @remarks
 * - The returned hook supports options like `fetchOnMount` and `refetchInterval`.
 */
export function createQuery<PayloadCreator extends PayloadCreatorBaseType>(
    options:
        | CreateQueryOptionsWithCacheKeyAndCondition<PayloadCreator>
        | CreateQueryOptionsWithCacheKey<PayloadCreator>
        | CreateQueryOptionsWithCondition<PayloadCreator>
        | CreateQueryOptions<PayloadCreator>,
): [
    (
        | UseQueryWithCacheKey<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>
        | UseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>
    ),
    (
        | CreateQueryImperativeHelpersWithCacheKeyAndCondition<
              GetPayloadArg<PayloadCreator>,
              GetReturned<PayloadCreator>
          >
        | CreateQueryImperativeHelpersWithCacheKey<
              GetPayloadArg<PayloadCreator>,
              GetReturned<PayloadCreator>
          >
        | CreateQueryImperativeHelpersWithCondition<
              GetPayloadArg<PayloadCreator>,
              GetReturned<PayloadCreator>
          >
        | CreateQueryImperativeHelpers<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator>>
    ),
    UseSuspenseQuery<GetPayloadArg<PayloadCreator>, GetReturned<PayloadCreator> | undefined>,
    UsePrefetchQuery<GetPayloadArg<PayloadCreator>>,
] {
    type PayloadArg = GetPayloadArg<PayloadCreator>;
    type Returned = GetReturned<PayloadCreator>;
    const { cacheDuration, retryOnMount } = options;

    // payload creator must not return undefined or RQ crashes
    const payloadCreatorWithSamePromiseWhilePending = withSamePromiseWhilePending((arg) =>
        options.payloadCreator(arg).then(fallbackToNull),
    );

    const invalidateAllCacheKeys = (
        opts?: Pick<InvalidateQueryFilters, 'refetchType'>,
    ): Promise<void> =>
        getQueryClient().invalidateQueries({
            queryKey: getQueryKey(options),
            refetchType: opts?.refetchType,
        });
    const invalidate = (arg?: PayloadArg): Promise<void> =>
        getQueryClient().invalidateQueries({ queryKey: getQueryKey(options, arg) });
    const selectSlice = (arg: PayloadArg): QueryState<Returned> => {
        const key = getQueryKey(options, arg);
        const rqSlice = getQueryClient().getQueryState<Query<Returned>>(key);
        const data = getQueryClient().getQueryData<Returned>(key);

        return {
            data: data ?? undefined,
            error: rqSlice?.error ?? null,
            lastUpdated: rqSlice?.dataUpdatedAt || null,
            isPending: rqSlice?.status === 'pending',
            consecutiveFails: rqSlice?.fetchFailureCount ?? 0,
            didInvalidate: rqSlice?.isInvalidated ?? false,
        };
    };

    const selectData = (arg: PayloadArg): Returned | undefined => {
        const key = getQueryKey(options, arg);
        // Backwards compatibility - Invalid casting - arg can be undefined
        return getQueryClient().getQueryData<Returned>(key);
    };
    const queryApi = (arg: PayloadArg): Promise<Returned> => {
        const key = getQueryKey(options, arg);
        return getQueryClient().fetchQuery({
            queryKey: key,
            queryFn: () => payloadCreatorWithSamePromiseWhilePending(arg),
            retry: createRetryHandler(),
            gcTime: Infinity,
            staleTime: 0,
        });
    };
    const getSliceId: GetSliceId<PayloadArg> = (arg) => getQueryKey(options, arg);

    const queryApiIfNeeded = async (arg: PayloadArg): Promise<Returned | undefined> => {
        const state = selectSlice(arg);

        if (options.condition && options.condition(arg, state) === false) {
            return undefined;
        }

        if (state.data && !state.didInvalidate) {
            return state.data;
        }

        return queryApi(arg);
    };
    const updateCache = (args: { id: PayloadArg; data: Returned | null }): void => {
        const key = getQueryKey(options, args.id);
        getQueryClient().setQueryData(key, args.data);
    };

    const conditionAdapter =
        options.condition &&
        ((conditionArg: PayloadArg) =>
            options.condition?.(conditionArg, selectSlice(conditionArg)));

    const useQueryLuna: UseQueryWithCacheKey<PayloadArg, Returned> = (arg, opts) => {
        const defaultQueryOptions = getCreateQueryDefaultOptions();
        const queryOptions = {
            ...defaultQueryOptions,
            ...opts,
        };

        const key: string[] = useMemo(() => getQueryKey(options, arg), [arg]);

        const enabled = useDynamicEnabled({
            args: arg,
            fetchOnMount: queryOptions?.fetchOnMount,
            condition: conditionAdapter,
        });

        const {
            fetchStatus,
            data,
            refetch: refetchUseQuery,
            error,
            isStale,
        } = useQuery<Returned>({
            queryKey: key,
            queryFn: () => payloadCreatorWithSamePromiseWhilePending(arg),
            enabled,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchInterval: queryOptions?.refetchInterval,
            refetchIntervalInBackground: false,
            retry: createRetryHandler(),
            retryOnMount: retryOnMount ?? getRetryOnMountInCaseOfError(key),
            gcTime: Infinity,
            staleTime: cacheDuration ?? Infinity,
            placeholderData: queryOptions?.keepPreviousData
                ? keepPreviousData
                : createPlaceholderDataHandler(key),
            throwOnError: queryOptions?.throwOnError ?? false,
        });

        const isPending = useMemo(
            () =>
                queryOptions?.keepPreviousData
                    ? !data && fetchStatus !== 'idle'
                    : fetchStatus === 'fetching',
            [data, fetchStatus, queryOptions?.keepPreviousData],
        );
        const refetch = useCallback(() => {
            return refetchUseQuery()
                .then((result) => result.data)
                .catch((err) => {
                    throw err?.toString?.();
                }) as Promise<Returned>;
        }, [refetchUseQuery]);

        const invalidateHook = useCallback(
            async (argOverride?: PayloadArg) => {
                const overrideKey = getQueryKey(options, argOverride ?? arg);
                const defaultKey = getQueryKey(options, arg);

                /*
                 * Hack since enabled false disables ALL background fetches. Only way to trigger re-fetch is to call it directly (no invalidate).
                 * This can be done only if override key & default key are the same.
                 */
                if (
                    queryOptions?.fetchOnMount === false &&
                    overrideKey.toString() === defaultKey.toString()
                ) {
                    await refetch();
                    return;
                }
                await invalidate(argOverride ?? arg);
            },
            [refetch, JSON.stringify(arg)],
        );

        return {
            data: data ?? undefined,
            refetch,
            isPending,
            error,
            didInvalidate: isStale,
            invalidateAllCacheKeys,
            invalidate: invalidateHook,
        };
    };

    const useSuspenseQueryLuna: UseSuspenseQuery<PayloadArg, Returned> = (arg, opts) => {
        const defaultQueryOptions = getCreateQueryDefaultOptions();
        const queryOptions = {
            ...defaultQueryOptions,
            ...opts,
        };

        const key: string[] = useMemo(() => getQueryKey(options, arg), [arg]);

        const {
            data,
            refetch: refetchUseQuery,
            isStale,
            isRefetching,
        } = useSuspenseQuery<Returned>({
            queryKey: key,
            queryFn: () => payloadCreatorWithSamePromiseWhilePending(arg),
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchInterval: queryOptions?.refetchInterval,
            refetchIntervalInBackground: false,
            retry: createRetryHandler(),
            retryOnMount,
            gcTime: Infinity,
            staleTime: cacheDuration ?? Infinity,
        });

        const refetch = useCallback(() => {
            return refetchUseQuery()
                .then((result) => result.data)
                .catch((err) => {
                    throw err?.toString?.();
                }) as Promise<Returned>;
        }, [refetchUseQuery]);

        const invalidateHook = useCallback(
            async (argOverride?: PayloadArg) => {
                const overrideKey = getQueryKey(options, argOverride ?? arg);
                const defaultKey = getQueryKey(options, arg);

                /*
                 * Hack since enabled false disables ALL background fetches. Only way to trigger re-fetch is to call it directly (no invalidate).
                 * This can be done only if override key & default key are the same.
                 */
                if (overrideKey.toString() === defaultKey.toString()) {
                    await refetch();
                    return;
                }
                await invalidate(argOverride ?? arg);
            },
            [refetch, JSON.stringify(arg)],
        );

        return {
            data,
            refetch,
            isRefetching,
            didInvalidate: isStale,
            invalidateAllCacheKeys,
            invalidate: invalidateHook,
        };
    };

    const useQueryLunaPrefetch: UsePrefetchQuery<PayloadArg> = (hookArg: PayloadArg) => {
        useEffect(() => {
            queryApiIfNeeded(hookArg).catch(() => {
                // Do nothing, as this is a prefetch operation
            });
        }, [hookArg]);
    };

    return [
        useQueryLuna,
        {
            queryApi,
            queryApiIfNeeded,
            invalidate,
            invalidateAllCacheKeys,
            selectSlice,
            selectData,
            updateCache,
            getSliceId,
        },
        useSuspenseQueryLuna,
        useQueryLunaPrefetch,
    ];
}
